import React, { Fragment, useState, useMemo, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Card, CardBody, Badge, Form } from 'reactstrap';
import { ApiRequest, ToastBuilder, ButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe } from '../../../../../../../../GlobalComponents/Helpers';
import { ChevronDown, ChevronRight, Package } from 'react-feather';

const SelectMappingsModal = forwardRef(({ jobData, jobResources, jobResourceMappings, defaultResourceMappings, onComplete, onSelectionChange }, ref) => {

    const [submitting, setSubmitting] = useState(false);
    const [selectedParents, setSelectedParents] = useState({});
    const [selectedChildren, setSelectedChildren] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    const sourceServiceId = jobData?.source_connection?.service_id;
    const destServiceId = jobData?.destination_connection?.service_id;

    // Build set of existing mapping keys from job
    const existingMappings = useMemo(() => {
        const mappings = jobResourceMappings?.current || [];
        return new Set(mappings.map(m => `${m.source_resource_id}-${m.destination_resource_id}`));
    }, [jobResourceMappings?.current]);

    const menuItems = useMemo(() => {
        return (defaultResourceMappings || [])
            .filter(g => g.source_service_id === sourceServiceId && g.destination_service_id === destServiceId)
            .map(g => ({ ...g, dependencies: typeof g.dependencies === 'string' ? parseJSONSafe(g.dependencies) || [] : g.dependencies || [] }))
            .map(g => ({ ...g, dependencies: [...g.dependencies].sort((a, b) => (a.hierarchy ?? 99) - (b.hierarchy ?? 99)) }))
            .sort((a, b) => (a.hierarchy ?? 99) - (b.hierarchy ?? 99));
    }, [defaultResourceMappings, sourceServiceId, destServiceId]);

    const depKey = (d) => `${d.source_resource_id}-${d.destination_resource_id}`;

    const isDepExisting = (dep) => existingMappings.has(depKey(dep));
    const isGroupExisting = (group) => existingMappings.has(group.resource_mapping);

    const toggleParent = (group) => {
        const key = group.resource_mapping;
        if (isGroupExisting(group)) return;
        setSelectedParents(prev => {
            const next = { ...prev };
            if (next[key]) {
                delete next[key];
                setSelectedChildren(p => { const n = { ...p }; delete n[key]; return n; });
            } else {
                next[key] = group;
                setExpandedGroups(p => {
                    setTimeout(() => {
                        const el = document.getElementById(`group-${key}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 50);
                    return { ...p, [key]: true };
                });
            }
            return next;
        });
    };

    // Find all parent groups that contain a given dep key
    const findParentsWithDep = (dk) => menuItems.filter(g => g.dependencies.some(d => depKey(d) === dk)).map(g => g.resource_mapping);

    const toggleChild = (parentKey, dep) => {
        if (isDepExisting(dep)) return;
        const key = depKey(dep);

        // Handle promoted parent being unchecked
        const isPromoted = !selectedChildren[parentKey]?.[key] && !!selectedParents[key];
        if (isPromoted) {
            setSelectedParents(p => { const n = { ...p }; delete n[key]; return n; });
            return;
        }

        const isSelecting = !selectedChildren[parentKey]?.[key];

        // When deselecting the last child of an existing parent, remove parent from selectedParents
        if (!isSelecting) {
            const currentChildren = { ...(selectedChildren[parentKey] || {}) };
            delete currentChildren[key];
            if (Object.keys(currentChildren).length === 0 && isGroupExisting(menuItems.find(g => g.resource_mapping === parentKey))) {
                setSelectedParents(p => { const n = { ...p }; delete n[parentKey]; return n; });
            }
        }

        setSelectedChildren(prev => {
            const next = { ...prev };

            // Sync across all parents that share this dep
            const allParentsWithDep = findParentsWithDep(key);
            allParentsWithDep.forEach(pk => {
                const pkDeps = { ...(next[pk] || {}) };
                if (isSelecting) {
                    pkDeps[key] = dep;
                } else {
                    delete pkDeps[key];
                }
                next[pk] = pkDeps;
            });

            // Auto-select the interacted parent when selecting a child
            if (isSelecting && !selectedParents[parentKey]) {
                const parentGroup = menuItems.find(g => g.resource_mapping === parentKey);
                if (parentGroup) {
                    setSelectedParents(p => ({ ...p, [parentKey]: parentGroup }));
                }
            }

            // Cross-group: if this child matches a top-level group, promote/demote
            const matchingGroup = menuItems.find(g => g.resource_mapping === key);
            if (matchingGroup) {
                if (isSelecting) {
                    setSelectedParents(p => ({ ...p, [key]: matchingGroup }));
                    setExpandedGroups(p => ({ ...p, [key]: true }));
                    // Remove from all parents' children since it's promoted
                    allParentsWithDep.forEach(pk => { delete next[pk][key]; });
                } else {
                    setSelectedParents(p => { const n = { ...p }; delete n[key]; return n; });
                }
            }

            return next;
        });
    };

    const toggleAllChildren = (parentKey, deps) => {
        const current = selectedChildren[parentKey] || {};
        const allSelected = deps.every(d => current[depKey(d)]);
        setSelectedChildren(prev => {
            if (allSelected) {
                // Deselecting all — also deselect any cross-group parents
                deps.forEach(d => {
                    const dk = depKey(d);
                    if (menuItems.find(g => g.resource_mapping === dk)) {
                        setSelectedParents(p => { const n = { ...p }; delete n[dk]; return n; });
                    }
                });
                // If parent is existing, also remove it from selectedParents since all children are deselected
                if (isGroupExisting(menuItems.find(g => g.resource_mapping === parentKey))) {
                    setSelectedParents(p => { const n = { ...p }; delete n[parentKey]; return n; });
                }
                return { ...prev, [parentKey]: {} };
            }
            // Auto-select existing parent when selecting children
            if (!selectedParents[parentKey]) {
                const parentGroup = menuItems.find(g => g.resource_mapping === parentKey);
                if (parentGroup) {
                    setSelectedParents(p => ({ ...p, [parentKey]: parentGroup }));
                }
            }
            const all = {};
            deps.forEach(d => {
                const dk = depKey(d);
                all[dk] = d;
                // Cross-group: auto-select matching parents
                const matchingGroup = menuItems.find(g => g.resource_mapping === dk);
                if (matchingGroup) {
                    setSelectedParents(p => ({ ...p, [dk]: matchingGroup }));
                    setExpandedGroups(p => ({ ...p, [dk]: true }));
                    // Remove from children since it's promoted to top-level
                    delete all[dk];
                }
            });
            return { ...prev, [parentKey]: all };
        });
    };

    const toggleExpand = (key) => {
        setExpandedGroups(prev => {
            const isExpanding = !prev[key];
            if (isExpanding) {
                setTimeout(() => {
                    const el = document.getElementById(`group-${key}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }, 50);
            }
            return { ...prev, [key]: !prev[key] };
        });
    };

    const handleSubmit = () => {
        const selections = Object.entries(selectedParents).map(([key, group]) => ({
            resource_mapping: group.resource_mapping,
            source_resource: group.source_resource,
            destination_resource: group.destination_resource,
            children: Object.values(selectedChildren[key] || {})
        }));

        if (selections.length === 0) return ToastBuilder('error', 'Please select at least one mapping');

        setSubmitting(true);
        ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId: jobData.id, actionName: 'apply-default-mappings' },
            body: {
                selections,
                sourceConnectionId: jobData.source_connection.id,
                destinationConnectionId: jobData.destination_connection.id,
                syncJobId: jobData.sync_job_id > 0 ? jobData.sync_job_id : null
            },
        }).then(res => {
            setSubmitting(false);
            if (res?.data?.success) {
                const loadingTime = 15000;
                ToastBuilder('success', 'Loading new Resources...', {}, loadingTime);
                setTimeout(() => onComplete?.(), loadingTime);
            } else {
                ToastBuilder('error', res?.data?.message || 'Failed to create mappings');
            }
        });
    };

    const newParentCount = Object.keys(selectedParents).filter(k => !existingMappings.has(k)).length;
    const uniqueChildKeys = new Set();
    Object.values(selectedChildren).forEach(deps => Object.keys(deps).forEach(k => uniqueChildKeys.add(k)));
    const childCount = uniqueChildKeys.size;
    const totalCount = newParentCount + childCount;

    useEffect(() => {
        if (!onSelectionChange) return;
        if (totalCount === 0) return onSelectionChange('Apply');
        let parts = [];
        if (newParentCount > 0) parts.push(`${newParentCount} Mapping${newParentCount !== 1 ? 's' : ''}`);
        if (childCount > 0) parts.push(`${childCount} Dep${childCount !== 1 ? 's' : ''}`);
        onSelectionChange(`Apply ${parts.join(' + ')}`);
    }, [newParentCount, childCount]);

    if (menuItems.length === 0) return <p className="text-muted text-center p-3">No default mappings available for this service combination.</p>;

    return (
        <Form id='apply-default-mappings-form' onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="mb-0">
                    Select resource mappings to add to this job.
                    <br /><small className="text-muted">Select a resource, then optionally expand to include dependencies.</small>
                </p>
                <Badge color={totalCount > 0 ? 'primary' : 'secondary'} pill>
                    {newParentCount > 0 ? `${newParentCount} resource${newParentCount !== 1 ? 's' : ''}` : ''}{newParentCount > 0 && childCount > 0 ? ' + ' : ''}{childCount > 0 ? `${childCount} dep${childCount !== 1 ? 's' : ''}` : ''}{totalCount === 0 ? '0 selected' : ''}
                </Badge>
            </div>

            <div style={{ maxHeight: 450, overflowY: 'auto' }}>
                {menuItems.map(group => {
                    const key = group.resource_mapping;
                    const isParentSelected = !!selectedParents[key];
                    const isExpanded = !!expandedGroups[key];
                    const deps = group.dependencies;
                    const childSelectedCount = deps.filter(d => selectedChildren[key]?.[depKey(d)]).length;
                    const groupLabel = group.source_resource === group.destination_resource
                        ? group.source_resource
                        : `${group.source_resource} → ${group.destination_resource}`;

                    return (
                        <Card key={key} id={`group-${key}`} className="mb-2" style={{ border: isParentSelected ? '1px solid #4a90d9' : undefined }}>
                            <CardBody className="p-0">
                                <div className="d-flex align-items-center justify-content-between p-2 section-card-header" style={{ cursor: 'pointer' }} onClick={() => toggleExpand(key)}>
                                    <div className="d-flex align-items-center" style={{ flex: 1 }}>
                                        <input type="checkbox" checked={isParentSelected || isGroupExisting(group)} disabled={isGroupExisting(group)} onChange={() => toggleParent(group)} className="me-2" style={{ width: 16, height: 16, cursor: isGroupExisting(group) ? 'not-allowed' : 'pointer' }} onClick={e => e.stopPropagation()} />
                                        <strong style={{ fontSize: 13, opacity: isGroupExisting(group) ? 0.5 : 1 }}>{groupLabel}</strong>
                                        <Badge color="secondary" pill className="ms-2" style={{ fontSize: 10 }}>{key}</Badge>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        {childSelectedCount > 0 && <Badge color="primary" pill className="me-2" style={{ fontSize: 10 }}>{childSelectedCount}/{deps.length}</Badge>}
                                        {deps.length > 0 && (
                                            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                                        )}
                                    </div>
                                </div>

                                {isExpanded && deps.length > 0 && (
                                    <div className="px-3 pb-2 pt-1">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <small className="text-muted fw-bold">Dependencies</small>
                                            <a href="#" style={{ fontSize: 11 }} onClick={(e) => { e.preventDefault(); toggleAllChildren(key, deps); }}>
                                                {deps.every(d => selectedChildren[key]?.[depKey(d)]) ? 'Deselect All' : 'Select All'}
                                            </a>
                                        </div>
                                        {deps.map(dep => {
                                            const dk = depKey(dep);
                                            const isSelected = !!selectedChildren[key]?.[dk];
                                            const isPromotedParent = !!selectedParents[dk];
                                            const isExisting = isDepExisting(dep);
                                            const promotedHasChildren = isPromotedParent && Object.keys(selectedChildren[dk] || {}).length > 0;
                                            const isChecked = isSelected || isPromotedParent || isExisting;
                                            const isLocked = isExisting || promotedHasChildren;
                                            return (
                                                <div
                                                    key={dk}
                                                    className={`d-flex align-items-center py-1 px-2 rounded mb-1 ${isChecked ? 'dmt-dep-selected' : ''}`}
                                                    style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isExisting ? 0.5 : 1 }}
                                                    onClick={() => !isLocked && toggleChild(key, dep)}
                                                >
                                                    <input type="checkbox" checked={isChecked} disabled={isLocked} readOnly className="me-2" style={{ width: 14, height: 14 }} />
                                                    <Package size={12} className="me-2 text-muted" />
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{ fontSize: 12 }}>{dep.description || dep.label}</span>
                                                        {dep.label && dep.description && <small className="text-muted ms-1">({dep.label})</small>}
                                                    </div>
                                                    {dep.is_setup ? <Badge color="info" pill style={{ fontSize: 9 }}>Setup</Badge> : <Badge color="secondary" pill style={{ fontSize: 9 }}>Migration</Badge>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            <div className="d-flex justify-content-end mt-3">
                <ButtonBuilder
                    label={submitting ? 'Applying...' : totalCount === 0 ? 'Apply' : `Apply ${[newParentCount > 0 ? `${newParentCount} Mapping${newParentCount !== 1 ? 's' : ''}` : '', childCount > 0 ? `${childCount} Dep${childCount !== 1 ? 's' : ''}` : ''].filter(Boolean).join(' + ')}`}
                    className="btn-primary"
                    disabled={totalCount === 0 || submitting}
                    onClick={handleSubmit}
                />
            </div>
        </Form>
    );
});

export default SelectMappingsModal;
